import * as PortalAction from "@src/Actions/PortalActions"
import {
  actions as GLOBAL_MODALS,
  actions as MODALS,
  GLOBAL_MODALS_KEYS,
} from "@src/Actions/Reducers/GlobalModalsReducer"
import { usePortalSize } from "@src/Components/Common/usePortalSize"
import Header from "@src/Components/Header/index"
import C from "@src/Controller"
import GlobalModals from "@src/GlobalModals"
import { tFeatures, tState } from "@src/Model/Model"
import MenuContainer from "@src/Portal/MenuContainer"
import MessageContainer from "@src/Portal/MessageContainer"
import ChangeLanguageConfirmationModal from "@src/Portal/PortalContainer/Components/ChangeLanguageConfirmationModal"
import SubscriptionBanner from "@src/Portal/SubscriptionBanner"
import { VIEW_PATHS } from "@src/Routes/constants"
import {
  triggerCybersecurityRoadBlockModalWatcher,
  updateCustomerPortalData,
} from "@src/Sagas"
import { hasTrialPlanExpiredSelector } from "@src/Selectors/utils"
import { ViewTypes } from "@src/types"
import { isBefore, parse, startOfDay } from "date-fns"
import * as _ from "lodash"
import * as React from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useLocation } from "react-router-dom"

import {
  NavbarSubscriptionBtn,
  PageContent,
  ZeguroSessionBanner,
} from "./styles"

interface IPortalContainer {
  children: React.ReactNode
  authenticated: boolean
  isMaintenance: boolean
}

const PortalContainer = ({
  children,
  authenticated,
  isMaintenance,
}: IPortalContainer) => {
  const dispatch = useDispatch()
  const {
    clientHeight,
    clientWidth,
    isDesktop,
    isMenuHidden,
    onBoarding,
    isFirstTimeLogin,
    isIncorrectURL,
  } = usePortalSize()
  const contentRef = React.useRef() as React.MutableRefObject<HTMLDivElement>
  const [menuIsOpen, setMenuIsOpen] = React.useState(false)
  const plans = useSelector((state: tState) => state.plans)

  const subscriptionId = useSelector((state: tState) => state.subscriptionId)

  const { companyProfile, isZeguroServiceSession } = useSelector(
    (state: tState) => state.portal,
  )

  const customerPortalData = useSelector(
    (store: tState) => store.customerPortalData.data,
  )
  const introViewed = customerPortalData?.introViewed

  const isChangeLanguageModalOpen = useSelector(
    (state: tState) => state.globalModals.languageChangeModalState.isOpen,
  )

  const hasSeenReleaseNotes =
    customerPortalData?.cybermaturity?.hasSeenReleaseNotes

  const uiRules = plans.channelObject.uiRules || subscriptionId.uiRules
  const history = useHistory()
  const { pathname } = useLocation()
  const onSubscriptionPage = pathname.includes(VIEW_PATHS.subscription)
  const onSettingsPage = pathname.includes(VIEW_PATHS.settings)

  const navigateToSubscription = () => {
    dispatch(PortalAction.setPortalMenu("subscription"))
    history.push(VIEW_PATHS.subscription)
  }

  const NAV_BAR_HEIGHT =
    !isDesktop &&
    !onBoarding &&
    authenticated &&
    !isFirstTimeLogin &&
    !isIncorrectURL
      ? 70
      : 0
  const MENU_WIDTH = isMenuHidden || !isDesktop ? 0 : 250

  const hasTrialExpired = useSelector(hasTrialPlanExpiredSelector)

  const cyberSecurityMonth =
    isBefore(
      startOfDay(new Date()),
      startOfDay(parse("01 Nov 2020", "dd MMM yyyy", new Date())),
    ) && plans.subscriptionStatus === "INTRIAL"

  const ZEGURO_SESSION_BANNER =
    isZeguroServiceSession && authenticated && isDesktop ? 48 : 0
  const TRIAL_BANNER_HEIGHT = !hasTrialExpired ? 0 : !isDesktop ? 80 : 50
  const CYBER_MONTH_BANNER = !cyberSecurityMonth ? 0 : !isDesktop ? 120 : 70
  let BANNER_HEIGHT = 0
  if (hasTrialExpired) {
    BANNER_HEIGHT += TRIAL_BANNER_HEIGHT
  }
  if (cyberSecurityMonth) {
    BANNER_HEIGHT += CYBER_MONTH_BANNER
  }

  const contentWidth = clientWidth - MENU_WIDTH
  const contentHeight =
    clientHeight -
    NAV_BAR_HEIGHT -
    (hasTrialExpired || cyberSecurityMonth ? BANNER_HEIGHT : 0)

  React.useEffect(() => {
    if (
      authenticated &&
      customerPortalData &&
      !hasTrialExpired &&
      plans.modules.cybermaturity === "ACTIVE"
    ) {
      if (
        !introViewed?.dashboard &&
        !hasSeenReleaseNotes &&
        !isZeguroServiceSession
      ) {
        dispatch(
          updateCustomerPortalData({
            cybermaturity: { hasSeenReleaseNotes: true },
          }),
        )
      } else if (!hasSeenReleaseNotes) {
        dispatch(
          MODALS.toggleGlobalModal(
            GLOBAL_MODALS_KEYS.cybersecurityReleaseNotes,
          ),
        )
      } else if (hasSeenReleaseNotes) {
        dispatch(triggerCybersecurityRoadBlockModalWatcher())
      }
    }
  }, [authenticated, hasSeenReleaseNotes])

  React.useEffect(() => {
    if (plans.status == "FAILED" && !_.isEmpty(companyProfile)) {
      C.toggleSubscribeModal(true)
    } else {
      C.toggleSubscribeModal(false)
    }
    const module = _.first(
      [
        "monitoring",
        "training",
        "compliance",
        "dashboard",
        "ratings",
        "cybermaturity",
        "cyberconcierge",
      ].filter((e) => {
        return _.startsWith(pathname, VIEW_PATHS[e])
      }),
    )
    if (
      module &&
      authenticated &&
      customerPortalData &&
      !introViewed?.[module as keyof tFeatures]
    ) {
      dispatch(GLOBAL_MODALS.changeIntroModal(module))
    }

    contentRef?.current?.scrollIntoView({
      block: "start",
    })
  }, [pathname, plans, authenticated, introViewed, customerPortalData])

  React.useEffect(() => {
    setMenuIsOpen(false)
  }, [pathname])

  return (
    <>
      {!onBoarding && !isFirstTimeLogin && !isIncorrectURL ? (
        <SubscriptionBanner
          OnSubscriptionPage={onSubscriptionPage}
          trialBannerheight={TRIAL_BANNER_HEIGHT}
          cyberSecurityMonthBannerHeight={CYBER_MONTH_BANNER}
          width={clientWidth}
          offsetLeft={0}
          hasTrialExpired={hasTrialExpired}
          cyberSecurityMonth={cyberSecurityMonth}
          goToSubscription={() => history.push(ViewTypes.plan)}
        />
      ) : null}
      <MenuContainer
        hidden={isMenuHidden}
        width={MENU_WIDTH}
        offset={(!onBoarding && TRIAL_BANNER_HEIGHT) || 0}
        isOpen={menuIsOpen}
        uiRules={uiRules}
        onClose={() => setMenuIsOpen(false)}
      />
      <MessageContainer />
      {!isMaintenance && (
        <Header
          height={NAV_BAR_HEIGHT}
          offsetTop={BANNER_HEIGHT}
          uiRules={uiRules}
          onBoarding={onBoarding}
          isFirstTimeLogin={isFirstTimeLogin}
          isIncorrectURL={isIncorrectURL}
          authenticated={authenticated}
          onOpenMenu={() => setMenuIsOpen(true)}
          rightAction={
            onSettingsPage ? (
              <NavbarSubscriptionBtn onClick={navigateToSubscription}>
                Manage Subscription
              </NavbarSubscriptionBtn>
            ) : undefined
          }
        />
      )}
      {isZeguroServiceSession && authenticated && isDesktop && (
        <ZeguroSessionBanner offset={MENU_WIDTH}>
          Attention! Zeguro service session is in progress!
        </ZeguroSessionBanner>
      )}
      {customerPortalData && !isFirstTimeLogin && !isIncorrectURL && (
        <GlobalModals />
      )}
      <PageContent
        ref={contentRef}
        height={contentHeight}
        width={contentWidth}
        paddingTop={NAV_BAR_HEIGHT + BANNER_HEIGHT || ZEGURO_SESSION_BANNER}
        marginLeft={MENU_WIDTH}
      >
        {children}
      </PageContent>

      {isChangeLanguageModalOpen && <ChangeLanguageConfirmationModal />}
    </>
  )
}
export default PortalContainer
